import React from 'react';
import { Classes, Checkbox, DialogStep, MultistepDialog, ButtonProps, Callout, Intent } from '@blueprintjs/core';

import { saveAsCSV } from '@f/utils/export';

import cls from './TemplateCreator.module.scss';

export interface TemplateFieldProps {
  name: string;
  type: string;
  displayName: string | undefined;
  disabled: boolean;
  description: string | React.FC | React.ReactNode;
  isSelected: (name: string) => boolean;
  toggleSelected: (name: string) => () => void;
}

function TemplateField({
  name,
  type,
  displayName,
  disabled,
  description,
  isSelected,
  toggleSelected,
}: TemplateFieldProps) {
  return (
    <Checkbox disabled={disabled} checked={isSelected(name)} onChange={toggleSelected(name)}>
      <>
        <b>{displayName || name}</b> <i>({type})</i>: {description}
      </>
    </Checkbox>
  );
}

TemplateField.defaultProps = {
  disabled: false,
  displayName: undefined,
};

interface TemplateCreatorProps {
  isOpen: boolean;
  onClose: () => void;
}

function TemplateCreator({ isOpen, onClose }: TemplateCreatorProps) {
  const [selectedFields, setSelectedFields] = React.useState<string[]>(['smiles', 'amount']);

  const isSelected = (fieldName: string): boolean => {
    return selectedFields.includes(fieldName);
  };

  const toggleSelected = (fieldName: string) => () => {
    if (isSelected(fieldName)) {
      setSelectedFields(selectedFields.filter(item => item !== fieldName));
    } else {
      setSelectedFields([...selectedFields, fieldName]);
    }
  };

  const DownloadButtonProps = {
    intent: Intent.PRIMARY,
    onMouseDown: () => {
      saveAsCSV([], 'M1_starting_materials_template', { columnHeaders: selectedFields });
    },
    text: 'Download',
  };

  const FinalButtonProps: Partial<ButtonProps> = {
    text: 'Close',
    onClick: onClose,
  };

  return (
    <MultistepDialog
      title="Starting Materials Dataset Template Creator"
      isOpen={isOpen}
      onClose={onClose}
      finalButtonProps={FinalButtonProps}
    >
      <DialogStep
        id="configure"
        title="Configure"
        panel={
          <div className={Classes.DIALOG_BODY}>
            <ConfigurePane isSelectedHandler={isSelected} toggleSelectedHandler={toggleSelected} />
          </div>
        }
      />
      <DialogStep
        id="download"
        title="Download"
        nextButtonProps={DownloadButtonProps}
        panel={
          <div className={Classes.DIALOG_BODY}>
            <DownloadPane selectedFields={selectedFields} />
          </div>
        }
      />
      <DialogStep
        id="ready"
        title="Ready"
        panel={
          <div className={Classes.DIALOG_BODY}>
            <h3>Done!</h3>
            <p>The template has been downloaded to your browser's download folder. You may now close this window.</p>
            <Callout intent={Intent.PRIMARY}>
              Please make sure not to remove column labels when filling out the template.
            </Callout>
          </div>
        }
      />
    </MultistepDialog>
  );
}

interface ConfigurePaneProps {
  isSelectedHandler: (name: string) => boolean;
  toggleSelectedHandler: (name: string) => () => void;
}

function ConfigurePane({ isSelectedHandler, toggleSelectedHandler }: ConfigurePaneProps) {
  return (
    <>
      <h3>Select fields that are relevant to your business needs</h3>
      <TemplateField
        name="smiles"
        type="string"
        displayName="SMILES"
        disabled
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description="SMILES formula"
      />
      <TemplateField
        name="amount"
        type="number"
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description="amount in milligrams"
      />
      <TemplateField
        name="price"
        type="number"
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description="price per given amount in USD"
      />
      <TemplateField
        name="availability_tier"
        type="number"
        displayName="availability tier"
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description={
          <>
            one of:
            <dl className={cls.definitionList}>
              <dt>1</dt>
              <dd>max 1-5 business days</dd>
              <dt>2</dt>
              <dd>max 2-10 business days</dd>
              <dt>3</dt>
              <dd>max 4 weeks</dd>
              <dt>4</dt>
              <dd>may require synthesis, max 12 weeks</dd>
              <dt>5</dt>
              <dd>longer or unknown</dd>
            </dl>
          </>
        }
      />
      <TemplateField
        name="compound_name"
        type="string"
        displayName="compound name"
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description="name or internal identifier of the compound."
      />
      <TemplateField
        name="cas_number"
        type="string"
        displayName="CAS number"
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description="CAS number of the compound"
      />
      <TemplateField
        name="supplier"
        type="string"
        isSelected={isSelectedHandler}
        toggleSelected={toggleSelectedHandler}
        description="supplier or vendor name"
      />
    </>
  );
}

interface DownloadPaneProps {
  selectedFields: string[];
}

function DownloadPane({ selectedFields }: DownloadPaneProps) {
  return (
    <>
      <h3>Download template</h3>
      <p>Your generated template will include the following fields:</p>
      <ul>
        {selectedFields.map(fieldName => (
          <li key={fieldName}>{fieldName}</li>
        ))}
      </ul>
    </>
  );
}

export default TemplateCreator;
